/**
 * Review Ready Page
 * Shows AI-generated review and instructions to post it
 * Public page - accessed after completing survey
 */

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Link,
  styled,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  ContentCopy,
  OpenInNew,
  Redeem,
  Star,
  StarBorder,
} from '@mui/icons-material';
import { markReviewAsPosted } from '../services/customerSurveyService';
import type { GeneratedReview } from '../types/review';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#F9FAFB',
  padding: theme.spacing(2),
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 10),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(5, 40),
  },
}));

const ContentContainer = styled(Box)({
  maxWidth: '960px',
  margin: '0 auto',
  width: '100%',
});

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 5),
  backgroundColor: '#FFFFFF',
  borderBottom: '1px solid #E5E7EB',
  borderRadius: '12px 12px 0 0',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
}));

const MainContent = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '0 0 12px 12px',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  padding: theme.spacing(3, 5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 10),
  },
}));

const LogoIcon = styled(Box)({
  width: '24px',
  height: '24px',
  color: '#4F46E5',
});

const StepIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: '#EEF2FF',
  color: '#4F46E5',
  flexShrink: 0,
});

const StepConnector = styled(Box)({
  width: '1px',
  backgroundColor: '#E5E7EB',
  flexGrow: 1,
  margin: '8px 0',
});

const ReviewCard = styled(Card)({
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  marginTop: '24px',
  backgroundColor: '#FFFFFF',
});

const ReviewReady: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get review data from navigation state
  const reviewData = location.state?.review as GeneratedReview | undefined;
  const campaignId = location.state?.campaignId as string | undefined;
  const uniqueToken = location.state?.uniqueToken as string | undefined;
  const accessHash = location.state?.accessHash as string | undefined;
  const contactInfo = location.state?.contactInfo as { email?: string; phone?: string } | undefined;
  const existingRewardDetails = location.state?.rewardDetails; // Reward details from survey

  // State
  const [isCopied, setIsCopied] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewText, setReviewText] = useState<string>(reviewData?.review_text || ''); // Editable review text
  const [reviewRating, setReviewRating] = useState<number | undefined>(undefined); // Optional rating

  // Update reviewText when reviewData changes
  React.useEffect(() => {
    if (reviewData?.review_text) {
      setReviewText(reviewData.review_text);
    }
  }, [reviewData]);

  // If no review data, redirect back
  React.useEffect(() => {
    if (!reviewData || !campaignId || !uniqueToken || !accessHash) {
      console.error('Missing review data, redirecting...');
      // Could redirect to an error page or back to survey
    }
  }, [reviewData, campaignId, uniqueToken, accessHash]);

  // Handlers
  const handleCopyReview = async () => {
    if (!reviewText.trim()) return;

    try {
      await navigator.clipboard.writeText(reviewText);
      setIsCopied(true);
      // Copy state will be reset on next action
    } catch (err) {
      console.error('Failed to copy text:', err);
      setError('Failed to copy review text');
    }
  };

  const handleGoToReview = () => {
    if (!reviewData?.platform_url) {
      console.error('No platform URL available');
      setError('Review platform URL is not available');
      return;
    }
    console.log('Opening review platform:', reviewData.platform, 'URL:', reviewData.platform_url);
    window.open(reviewData.platform_url, '_blank', 'noopener,noreferrer');
  };

  // Helper function to capitalize platform name
  const getPlatformName = () => {
    if (!reviewData?.platform) return 'Review Platform';
    const platform = reviewData.platform.toLowerCase();
    // Capitalize first letter of each word
    return platform
      .split(/[\s_-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleMarkAsPosted = async () => {
    if (!campaignId || !uniqueToken || !accessHash || !reviewData) {
      setError('Missing required information to confirm review');
      return;
    }

    // Validate review_text is provided
    if (!reviewText.trim()) {
      setError('Please paste the review text you posted');
      return;
    }

    try {
      setIsConfirming(true);
      setError(null);

      console.log('📝 Confirming review posted...');

      // Prepare payload - review_rating is optional
      const payload: { review_url: string; review_text: string; review_rating?: number } = {
        review_url: reviewData.platform_url,
        review_text: reviewText.trim(),
      };

      // Add review_rating only if provided
      if (reviewRating !== undefined && reviewRating > 0) {
        payload.review_rating = reviewRating;
      }

      // Call the API to mark review as posted
      const response = await markReviewAsPosted(
        campaignId,
        uniqueToken,
        accessHash,
        payload
      );

      if (response.success) {
        console.log('✅ Review confirmed successfully');

        // Navigate to thank you page with reward details and IDs
        // Use reward details from confirm API or fallback to survey reward details
        navigate('/survey/thank-you', {
          state: { 
            rewardDetails: response.data?.reward_details || existingRewardDetails || null,
            campaignId,
            uniqueToken,
            accessHash,
            contactInfo, // Pass contact info for pre-populating reward form
          },
        });
      } else {
        throw new Error(response.message || 'Failed to confirm review');
      }
    } catch (err) {
      console.error('❌ Error confirming review:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to confirm review. Please try again.'
      );
    } finally {
      setIsConfirming(false);
    }
  };

  if (!reviewData) {
    return (
      <PageContainer>
        <ContentContainer>
          <Alert severity="error" sx={{ mt: 4, borderRadius: '12px', fontFamily: 'Inter' }}>
            <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Inter', fontWeight: 600 }}>
              No Review Data Found
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Inter' }}>
              Please complete the survey first to generate your review.
            </Typography>
          </Alert>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        {/* Header */}
        <Header>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LogoIcon>
              <svg fill="currentColor" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_6_319)">
                  <path d="M8.57829 8.57829C5.52816 11.6284 3.451 15.5145 2.60947 19.7452C1.76794 23.9758 2.19984 28.361 3.85056 32.3462C5.50128 36.3314 8.29667 39.7376 11.8832 42.134C15.4698 44.5305 19.6865 45.8096 24 45.8096C28.3135 45.8096 32.5302 44.5305 36.1168 42.134C39.7033 39.7375 42.4987 36.3314 44.1494 32.3462C45.8002 28.361 46.2321 23.9758 45.3905 19.7452C44.549 15.5145 42.4718 11.6284 39.4217 8.57829L24 24L8.57829 8.57829Z" />
                </g>
                <defs>
                  <clipPath id="clip0_6_319">
                    <rect fill="white" height="48" width="48" />
                  </clipPath>
                </defs>
              </svg>
            </LogoIcon>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Inter', color: '#111827' }}>
              RiteReview
            </Typography>
          </Box>
        </Header>

        {/* Main Content */}
        <MainContent>
          {/* Title */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.875rem', sm: '2rem' },
                mb: 1.5,
                pt: 3,
                fontFamily: 'Inter',
                color: '#111827',
              }}
            >
              Your Review is Ready!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
              Follow these simple steps to post your review and earn your reward.
            </Typography>
          </Box>

          {/* Steps */}
          <Box sx={{ px: { xs: 2, sm: 4 }, mb: 4 }}>
            {/* Step 1 */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <StepIcon>
                  <ContentCopy fontSize="small" />
                </StepIcon>
                <StepConnector />
              </Box>
              <Box sx={{ pt: 1, pb: 4 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, fontFamily: 'Inter', color: '#111827' }}>
                  1. Copy the review
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
                  Use the button below to copy the AI-generated text.
                </Typography>
              </Box>

              {/* Step 2 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <StepIcon>
                  <OpenInNew fontSize="small" />
                </StepIcon>
                <StepConnector />
              </Box>
              <Box sx={{ pt: 1, pb: 4 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, fontFamily: 'Inter', color: '#111827' }}>
                  2. Paste it on {getPlatformName()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
                  Click the "Go to {getPlatformName()}" button to open the review page.
                </Typography>
              </Box>

              {/* Step 3 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <StepIcon>
                  <Redeem fontSize="small" />
                </StepIcon>
              </Box>
              <Box sx={{ pt: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, fontFamily: 'Inter', color: '#111827' }}>
                  3. Return for your reward
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
                  After posting, come back here to claim your reward.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Review Card */}
          <Box sx={{ px: 2, mt: 6 }}>
            <ReviewCard>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 3, letterSpacing: '-0.015em', fontFamily: 'Inter', color: '#111827' }}
                >
                  AI-Generated Review
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Review text will appear here. You can edit it if needed..."
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        fontFamily: 'Inter',
                        fontStyle: 'italic',
                        lineHeight: 1.7,
                        '& fieldset': {
                          borderColor: '#D1D5DB',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9CA3AF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4F46E5',
                        },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={handleCopyReview}
                    sx={{
                      minWidth: { xs: '100%', sm: '100px' },
                      textTransform: 'none',
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      borderRadius: '8px',
                      borderColor: '#D1D5DB',
                      color: '#4F46E5',
                      backgroundColor: '#EEF2FF',
                      '&:hover': {
                        backgroundColor: '#E0E7FF',
                        borderColor: '#4F46E5',
                      },
                    }}
                  >
                    {isCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'Inter', color: '#6B7280', fontWeight: 500 }}
                  >
                    Rating (optional):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <Box
                        key={rating}
                        onClick={() => setReviewRating(reviewRating === rating ? undefined : rating)}
                        sx={{
                          cursor: 'pointer',
                          color: reviewRating && rating <= reviewRating ? '#FBBF24' : '#D1D5DB',
                          transition: 'color 0.2s',
                          '&:hover': {
                            color: '#FBBF24',
                          },
                        }}
                      >
                        {reviewRating && rating <= reviewRating ? (
                          <Star sx={{ fontSize: '28px' }} />
                        ) : (
                          <StarBorder sx={{ fontSize: '28px' }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </ReviewCard>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mt: 4,
              px: 2,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleGoToReview}
              fullWidth
              disabled={!reviewData?.platform_url}
              sx={{
                maxWidth: '400px',
                textTransform: 'none',
                borderRadius: '8px',
                py: 1.5,
                fontWeight: 600,
                fontSize: '1rem',
                fontFamily: 'Inter',
                backgroundColor: '#4F46E5',
                boxShadow: '0px 4px 12px rgba(79, 70, 229, 0.25)',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#4338CA',
                  transform: 'translateY(-2px)',
                  boxShadow: '0px 6px 16px rgba(79, 70, 229, 0.35)',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#9CA3AF',
                },
              }}
            >
              Go to {getPlatformName()} Review Page
            </Button>

            <Button
              variant="text"
              onClick={handleMarkAsPosted}
              disabled={isConfirming || !reviewText.trim()}
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
                fontFamily: 'Inter',
                fontWeight: 500,
                color: '#6B7280',
                '&:hover': {
                  color: '#4F46E5',
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
                '&.Mui-disabled': {
                  color: '#D1D5DB',
                },
              }}
            >
              {isConfirming ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1, color: '#4F46E5' }} />
                  Confirming...
                </>
              ) : (
                "I've posted my review"
              )}
            </Button>
          </Box>
        </MainContent>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ mt: 3, borderRadius: '12px', fontFamily: 'Inter' }}
          >
            {error}
          </Alert>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default ReviewReady;

